// =============================================================================
// LP Express — "Temecula Passport (Mystery Drop)" template renderer.
//
// Renders a `generatedContent.temeculaPassport` blob (see
// shared/lp-express-temecula-passport) as the imported Gaffle mystery-drop
// lead-capture page: scrolling ticker → header (logo) → hero + mystery card +
// coupon panel → poster → how the drop works → what's inside → gallery →
// limited banner → FAQ → bottom CTA + coupon panel → visit the pour bar →
// footer → sticky bottom bar.
//
//  • Copy is fully editable via the shared path-based `setText` ops (paths
//    like ["temeculaPassport","hero","headline"]).
//  • Sections reorder/hide/delete through ONE page-level LpReorderGroup +
//    per-section EditableElement.
//  • Imagery: the export's asset binaries were not included, so photo
//    surfaces are AI slots (generatedContent.temeculaPassportImages, per-slot
//    spinner/failed/retry) with the usual media-picker/upload override; the
//    logo slots fall back to the real brand logo.
//  • Lead capture: the two coupon panels are INERT in the builder (copy stays
//    editable) and LIVE only on the published public page, where
//    `ctx.publicLeadContext` carries {brandSlug, slug} and submissions POST to
//    /api/public/lp-express/:brandSlug/:slug/leads. Unlike the Lead Magnet's
//    per-lead coupons, the success card reveals ONE static, editable secret
//    code (couponPanel.success.code) with a copy-to-clipboard button; the
//    per-lead tracking code stays server-side and is never displayed. The
//    export's Meta Pixel is intentionally omitted.
//  • Per-section copy regen anchors on each section heading with dynamic block
//    keys `temecula-passport-<sectionKey>` (see
//    regenerate-temecula-passport-section).
// =============================================================================
import { useState } from "react";
import {
  ArrowRight, Check, CheckCircle2, Clock, Coffee, Copy, Flag, ImageIcon,
  Instagram, KeyRound, Leaf, Loader2, Mail, MapPin, Milk, Minus, PartyPopper,
  Phone, RotateCw, Sparkles, Star, Store, X,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  LpReorderGroup,
  LpImageActions,
  LinkUrlPopover,
  useLpEditor,
  useLpMediaOverride,
} from "@/components/lp-editor";
import {
  normalizeLpTemeculaPassportContent,
  lpTemeculaPassportSecretCode,
  splitLpTemeculaHeadline,
  LP_TEMECULA_PASSPORT_SECTIONS,
  LP_TEMECULA_PASSPORT_IMAGE_SLOTS,
  type LpTemeculaPassportContent,
} from "@shared/lp-express-temecula-passport";

// AI-generatable slot keys (mystery-card, poster, ingredients-feature,
// gallery-0..3, pourbar-0..2). Anything else falls back to the plain
// upload/media-picker placeholder.
const TP_AI_SLOT_KEYS = new Set(LP_TEMECULA_PASSPORT_IMAGE_SLOTS.map((s) => s.key));

export type TemeculaPassportCtx = {
  brand: any;
  generatedContent: any;
  logoUrl: string | null;
  typography?: { heading?: string; body?: string } | any;
  /** Retry a single failed AI image slot (temeculaPassportImages). In-app
   *  builder only — the public/published page leaves it undefined so failed
   *  slots stay as static placeholders. */
  onRetrySlot?: (mergeKey: "temeculaPassportImages", slotKey: string) => void;
  /** Present ONLY on the published public page — carries the route params the
   *  coupon panels need to POST to the public lead-capture endpoint. When
   *  undefined (builder/preview) the forms render inert. */
  publicLeadContext?: { brandSlug: string; slug: string };
};

// ── Palette ───────────────────────────────────────────────────────────────
// Color roles resolve to the BRAND's palette via the same `--color-brand-*`
// CSS variables the cold-drip/coffee-shop templates use (brandCssVarsV2
// applies them at the renderer root). The var() fallbacks are the original
// Gaffle export hexes.
const RED = "var(--color-brand-coral, #AE1F2E)";
const BLUE = "var(--color-brand-navy, #0BB6D5)";
const WINE = "var(--color-brand-deepnavy, #600A2C)";
const NAVY = "var(--color-brand-navyaccent, #1B2A5B)";
const CREAM = "var(--color-brand-cream, #FDF4F0)";
const GOLD = "var(--color-brand-goldaccent, #E8B23A)";
const INK = "#1A1A1A";
const MUTED = "#6B6B6B";
const BORDER = "rgba(96,10,44,0.10)";

// "Oaxaca Prickly" is served via @font-face in index.css (licensed binary at
// client/public/fonts/OaxacaPrickly.otf); Georgia is the load-failure
// fallback. The export renders its display type in italic.
const DISPLAY_FONT = '"Oaxaca Prickly", Georgia, "Times New Roman", serif';

const TP_FORM_ANCHOR = "temecula-passport-coupon";

// ── Media slots ──────────────────────────────────────────────────────────────

function tpMediaKey(slot: string): string {
  return `temecula-passport:img:${slot}`;
}

function MediaSlot({
  slot,
  label,
  className,
  style,
  testId,
  fallbackUrl,
  aiSlot,
  onRegenerate,
}: {
  slot: string;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  testId: string;
  fallbackUrl?: string | null;
  aiSlot?: { imageUrl?: string | null; imageStatus?: string | null; altText?: string | null } | null;
  onRegenerate?: () => void;
}) {
  const overrideUrl = useLpMediaOverride(tpMediaKey(slot));
  const editor = useLpEditor();
  const actions = (
    <LpImageActions
      slotKey={tpMediaKey(slot)}
      canRegenerate={!!onRegenerate}
      onRegenerate={onRegenerate}
      testIdPrefix={testId}
    />
  );
  const aiUrl =
    aiSlot?.imageStatus === "ready" && aiSlot.imageUrl ? aiSlot.imageUrl : null;
  const url = overrideUrl || aiUrl || fallbackUrl || null;
  if (url) {
    return (
      <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
        <img
          src={url}
          alt={aiSlot?.altText || label || ""}
          loading="lazy"
          className={className}
          style={{ objectFit: "cover", display: "block", width: "100%", height: "100%", ...style }}
          data-testid={testId}
        />
        {actions}
      </div>
    );
  }
  return (
    <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
      <div
        className={className}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 6, width: "100%", height: "100%", minHeight: 120, backgroundColor: CREAM,
          border: `1px dashed ${BORDER}`, borderRadius: 12, ...style,
        }}
        data-testid={testId}
      >
        {aiSlot?.imageStatus === "pending" ? (
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: MUTED }} />
        ) : aiSlot?.imageStatus === "failed" && editor && onRegenerate ? (
          <button
            type="button"
            onClick={onRegenerate}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
            style={{ color: MUTED, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
            data-testid={`${testId}-retry`}
          >
            <RotateCw className="h-3 w-3" /> Retry image
          </button>
        ) : (
          <ImageIcon className="h-5 w-5" style={{ color: MUTED }} />
        )}
        {editor && aiSlot?.imageStatus !== "pending" && (
          <span style={{ fontSize: 11, color: MUTED, textAlign: "center", padding: "0 8px" }}>
            {aiSlot?.imageStatus === "failed"
              ? "Image generation failed"
              : label || "Upload or pick an image"}
          </span>
        )}
      </div>
      {actions}
    </div>
  );
}

// Section shell: EditableElement wrapper. Fine-grained regen still anchors on
// each heading, but the section hover toolbar ALSO offers a compact Regenerate
// control (parity with the long-form section toolbar). "header" is logo-only —
// the server rejects it as having no regenerable copy — so it gets none.
function Shell({
  k,
  children,
  style,
}: {
  k: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <EditableElement
      elementKey={`temecula-passport-${k}`}
      toolbarSide="left"
      regenBlockKey={k === "header" ? undefined : `temecula-passport-${k}`}
    >
      <section style={style} data-testid={`temecula-passport-section-${k}`}>
        {children}
      </section>
    </EditableElement>
  );
}

const wrap: React.CSSProperties = { maxWidth: 1100, margin: "0 auto", padding: "0 20px" };

function Eyebrow({
  value, path, testId, color = NAVY, center,
}: {
  value: string; path: (string | number)[]; testId: string; color?: string; center?: boolean;
}) {
  return (
    <EditableText
      value={value}
      path={path}
      as="div"
      style={{
        fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
        color, marginBottom: 10, textAlign: center ? "center" : undefined,
      }}
      placeholder="Eyebrow"
      testId={testId}
    />
  );
}

function Heading({
  value, path, testId, regenKey, color = WINE, size = 40, center,
}: {
  value: string; path: (string | number)[]; testId: string; regenKey: string;
  color?: string; size?: number; center?: boolean;
}) {
  return (
    <EditableText
      value={value}
      path={path}
      as="h2"
      style={{
        fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: size, lineHeight: 1.08,
        fontWeight: 800, color, margin: "0 0 14px", letterSpacing: "-0.01em",
        textAlign: center ? "center" : undefined,
      }}
      placeholder="Heading"
      testId={testId}
      regenBlockKey={regenKey}
      regenAnchor
    />
  );
}

// ── Coupon panel ─────────────────────────────────────────────────────────────
// Editable copy in the builder (form controls inert), live submission on the
// published page when publicLeadContext is provided. Success replaces the
// form with the static-secret-code reveal card (copy-to-clipboard).

function SuccessCard({
  tp,
  P,
  rk,
  idSuffix,
  alreadyRegistered,
  editable,
}: {
  tp: LpTemeculaPassportContent;
  P: (...p: (string | number)[]) => (string | number)[];
  rk: (key: string) => string;
  idSuffix: string;
  alreadyRegistered: boolean;
  editable: boolean;
}) {
  const sc = tp.couponPanel.success;
  const code = lpTemeculaPassportSecretCode(sc.code);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (editable) return;
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {},
    );
  };
  return (
    <div
      style={{
        backgroundColor: "#fff", borderRadius: 18, padding: "28px 24px",
        border: `2px solid ${BLUE}`, boxShadow: "0 10px 30px rgba(96,10,44,0.10)", textAlign: "center",
      }}
      data-testid={`card-temecula-passport-success-${idSuffix}`}
    >
      <PartyPopper className="h-10 w-10" style={{ color: BLUE, margin: "0 auto" }} />
      {editable ? (
        <div style={{ margin: "10px 0 6px" }}>
          <EditableText value={sc.headingNew} path={P("couponPanel", "success", "headingNew")} as="div" style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: 30, lineHeight: 1.05, color: WINE }} placeholder="Success heading (new)" testId={`text-temecula-passport-success-heading-new-${idSuffix}`} regenBlockKey={rk("hero")} />
          <EditableText value={sc.headingReturning} path={P("couponPanel", "success", "headingReturning")} as="div" style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: 20, lineHeight: 1.1, color: WINE, opacity: 0.7, marginTop: 4 }} placeholder="Success heading (returning)" testId={`text-temecula-passport-success-heading-returning-${idSuffix}`} regenBlockKey={rk("hero")} />
        </div>
      ) : (
        <div style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: 32, lineHeight: 1.05, color: WINE, margin: "10px 0 6px" }} data-testid={`text-temecula-passport-success-heading-${idSuffix}`}>
          {alreadyRegistered ? tp.couponPanel.success.headingReturning : tp.couponPanel.success.headingNew}
        </div>
      )}
      <EditableText
        value={sc.body}
        path={P("couponPanel", "success", "body")}
        as="p"
        multiline
        style={{ fontSize: 15, lineHeight: 1.55, color: WINE, opacity: 0.85, margin: "0 0 20px" }}
        placeholder="Success body"
        testId={`text-temecula-passport-success-body-${idSuffix}`}
        regenBlockKey={rk("hero")}
      />
      <EditableText
        value={sc.codeLabel}
        path={P("couponPanel", "success", "codeLabel")}
        as="div"
        style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: NAVY, marginBottom: 8 }}
        placeholder="Code label"
        testId={`text-temecula-passport-code-label-${idSuffix}`}
        regenBlockKey={rk("hero")}
      />
      <button
        type="button"
        onClick={copy}
        title={editable ? undefined : "Tap to copy"}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap",
          gap: 10, maxWidth: "100%", minWidth: 0, boxSizing: "border-box",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "clamp(18px, 5.5vw, 24px)", fontWeight: 800, letterSpacing: "0.04em",
          color: WINE, backgroundColor: CREAM, border: `2px dashed ${RED}`, borderRadius: 12,
          padding: "16px 20px", cursor: editable ? "default" : "pointer",
        }}
        data-testid={`button-temecula-passport-code-${idSuffix}`}
      >
        {editable ? (
          <EditableText value={sc.code} path={P("couponPanel", "success", "code")} as="span" noHideDelete placeholder="SECRETCODE" testId={`text-temecula-passport-code-${idSuffix}`} />
        ) : (
          <span style={{ overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 }} data-testid={`text-temecula-passport-code-${idSuffix}`}>{code}</span>
        )}
        {copied ? <Check className="h-4 w-4" style={{ color: RED }} /> : <Copy className="h-4 w-4" style={{ color: RED, opacity: 0.7 }} />}
      </button>
      <p style={{ fontSize: 13, opacity: 0.7, margin: "18px 0 0", color: WINE }}>
        {copied ? tp.couponPanel.success.copiedPrefix : ""}
        <EditableText value={sc.copyHint} path={P("couponPanel", "success", "copyHint")} as="span" multiline placeholder="Copy hint" testId={`text-temecula-passport-copy-hint-${idSuffix}`} regenBlockKey={rk("hero")} />
      </p>
    </div>
  );
}

function CouponPanel({
  tp,
  P,
  rk,
  publicLeadContext,
  idSuffix,
  anchor,
  showSuccessEditor,
}: {
  tp: LpTemeculaPassportContent;
  P: (...p: (string | number)[]) => (string | number)[];
  rk: (key: string) => string;
  publicLeadContext?: { brandSlug: string; slug: string };
  idSuffix: string;
  anchor?: boolean;
  /** Render the (shared-path) editable success card under the form in the
   *  builder — only on ONE panel instance to avoid duplicate editors. */
  showSuccessEditor?: boolean;
}) {
  const editor = useLpEditor();
  const cp = tp.couponPanel;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ alreadyRegistered: boolean } | null>(null);

  const live = !!publicLeadContext && !editor;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!live || pending) return;
    setError(null);
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError(cp.errors.missingFields);
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      setError(cp.errors.shortPhone);
      return;
    }
    setPending(true);
    try {
      const res = await fetch(
        `/api/public/lp-express/${encodeURIComponent(publicLeadContext!.brandSlug)}/${encodeURIComponent(publicLeadContext!.slug)}/leads`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ firstName: name.trim(), email: email.trim(), phone: phone.trim() }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.couponCode) {
        setError(typeof data?.message === "string" && data.message ? data.message : cp.errors.generic);
        return;
      }
      setDone({ alreadyRegistered: !!data.alreadyRegistered || !!data.existing });
    } catch {
      setError(cp.errors.generic);
    } finally {
      setPending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "13px 14px", fontSize: 15, borderRadius: 10, boxSizing: "border-box",
    border: "1.5px solid rgba(96,10,44,0.18)", backgroundColor: CREAM, color: WINE, outline: "none",
  };

  if (done) {
    return (
      <div id={anchor ? TP_FORM_ANCHOR : undefined} style={{ scrollMarginTop: 80 }}>
        <SuccessCard tp={tp} P={P} rk={rk} idSuffix={idSuffix} alreadyRegistered={done.alreadyRegistered} editable={false} />
      </div>
    );
  }

  return (
    <div id={anchor ? TP_FORM_ANCHOR : undefined} style={{ scrollMarginTop: 80 }}>
      <form
        onSubmit={submit}
        noValidate
        style={{
          backgroundColor: "#fff", borderRadius: 18, padding: "26px 24px",
          border: `1px solid ${BORDER}`, boxShadow: "0 10px 30px rgba(96,10,44,0.08)",
        }}
        data-testid={`card-temecula-passport-coupon-${idSuffix}`}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <KeyRound className="h-3.5 w-3.5" style={{ color: RED, flexShrink: 0 }} />
          <EditableText value={cp.eyebrow} path={P("couponPanel", "eyebrow")} as="span" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: RED }} placeholder="Eyebrow" testId={`text-temecula-passport-form-eyebrow-${idSuffix}`} regenBlockKey={rk("hero")} />
        </div>
        <EditableText
          value={cp.heading}
          path={P("couponPanel", "heading")}
          as="h3"
          style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: 30, lineHeight: 1.05, color: WINE, margin: "0 0 14px" }}
          placeholder="Form heading"
          testId={`text-temecula-passport-form-heading-${idSuffix}`}
          regenBlockKey={rk("hero")}
        />
        <EditableText
          value={cp.body}
          path={P("couponPanel", "body")}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.5, color: WINE, opacity: 0.75, margin: "0 0 16px" }}
          placeholder="Form body"
          testId={`text-temecula-passport-form-body-${idSuffix}`}
          regenBlockKey={rk("hero")}
        />
        {cp.fields.map((f, i) => (
          <div key={i} style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: WINE, opacity: 0.7, marginBottom: 6 }}>
              <EditableText value={f.label} path={P("couponPanel", "fields", i, "label")} as="span" placeholder="Label" testId={`text-temecula-passport-form-label-${i}-${idSuffix}`} regenBlockKey={rk("hero")} />
            </label>
            {editor ? (
              <div style={{ ...inputStyle, color: MUTED, cursor: "default" }} data-testid={`input-temecula-passport-${i}-${idSuffix}`}>
                <EditableText value={f.placeholder} path={P("couponPanel", "fields", i, "placeholder")} as="span" placeholder="Placeholder" testId={`text-temecula-passport-form-placeholder-${i}-${idSuffix}`} regenBlockKey={rk("hero")} />
              </div>
            ) : (
              <input
                type={i === 1 ? "email" : i === 2 ? "tel" : "text"}
                value={i === 0 ? name : i === 1 ? email : phone}
                onChange={(e) => (i === 0 ? setName : i === 1 ? setEmail : setPhone)(e.target.value)}
                placeholder={f.placeholder}
                autoComplete={i === 0 ? "given-name" : i === 1 ? "email" : "tel"}
                disabled={!live || pending}
                style={inputStyle}
                data-testid={`input-temecula-passport-${i === 0 ? "name" : i === 1 ? "email" : "phone"}-${idSuffix}`}
              />
            )}
          </div>
        ))}
        {error && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, fontWeight: 600, color: RED, marginBottom: 10 }} data-testid={`text-temecula-passport-form-error-${idSuffix}`}>
            <X className="h-4 w-4" style={{ flexShrink: 0, marginTop: 1 }} /> {error}
          </div>
        )}
        <button
          type="submit"
          disabled={!live || pending}
          style={{
            display: "block", width: "100%", marginTop: 6, padding: "16px 18px", backgroundColor: RED,
            color: "#fff", fontSize: 14, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase",
            textAlign: "center", borderRadius: 10, border: "none",
            cursor: live ? "pointer" : "default", opacity: pending ? 0.7 : 1,
          }}
          data-testid={`button-temecula-passport-submit-${idSuffix}`}
        >
          {pending ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Loader2 className="h-4 w-4 animate-spin" /> {cp.submitPending}
            </span>
          ) : (
            <EditableText value={cp.submit} path={P("couponPanel", "submit")} as="span" noHideDelete placeholder="Submit" testId={`text-temecula-passport-submit-label-${idSuffix}`} regenBlockKey={rk("hero")} />
          )}
        </button>
        <EditableText
          value={cp.smsConsent}
          path={P("couponPanel", "smsConsent")}
          as="p"
          multiline
          style={{ fontSize: 11, lineHeight: 1.4, color: WINE, opacity: 0.55, margin: "12px 0 0" }}
          placeholder="SMS consent"
          testId={`text-temecula-passport-sms-consent-${idSuffix}`}
          regenBlockKey={rk("hero")}
        />
        <EditableText
          value={cp.footnote}
          path={P("couponPanel", "footnote")}
          as="p"
          multiline
          style={{ fontSize: 12, lineHeight: 1.45, color: WINE, opacity: 0.6, textAlign: "center", margin: "12px 0 0" }}
          placeholder="Footnote"
          testId={`text-temecula-passport-form-footnote-${idSuffix}`}
          regenBlockKey={rk("hero")}
        />
      </form>
      {editor && showSuccessEditor && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED, margin: "0 0 8px", textAlign: "center" }}>
            Success card (shown after signup)
          </div>
          <SuccessCard tp={tp} P={P} rk={rk} idSuffix={`${idSuffix}-editor`} alreadyRegistered={false} editable />
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const STEP_ICONS = [KeyRound, Clock, Store];
const INGREDIENT_ICONS = [Coffee, Leaf, Milk, Sparkles];

export default function LpExpressTemeculaPassport({ ctx }: { ctx: TemeculaPassportCtx }) {
  const tp: LpTemeculaPassportContent = normalizeLpTemeculaPassportContent(
    ctx.generatedContent?.temeculaPassport,
  );
  const editor = useLpEditor();

  const rk = (key: string) => `temecula-passport-${key}`;
  const P = (...p: (string | number)[]) => ["temeculaPassport", ...p];

  // AI image slots (generatedContent.temeculaPassportImages, keyed by slot key).
  const aiSlots: Record<string, { imageUrl?: string | null; imageStatus?: string | null; altText?: string | null }> = {};
  for (const s of (ctx.generatedContent?.temeculaPassportImages ?? []) as any[]) {
    if (s?.key) aiSlots[s.key] = s;
  }
  const retrySlot = ctx.onRetrySlot
    ? (slotKey: string) =>
        TP_AI_SLOT_KEYS.has(slotKey)
          ? () => ctx.onRetrySlot!("temeculaPassportImages", slotKey)
          : undefined
    : (_slotKey: string) => undefined;

  const scrollToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    if (editor) return;
    const doc = (e.currentTarget as HTMLElement).ownerDocument;
    doc.getElementById(TP_FORM_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const renderers: Record<string, () => React.ReactNode> = {
    ticker: () => (
      <Shell k="ticker" style={{ backgroundColor: NAVY, borderTop: `2px solid ${BLUE}`, borderBottom: `2px solid ${BLUE}`, overflow: "hidden" }}>
        {/* Single-line scrolling marquee (matches the reference site). Two copies
            of the item run back-to-back; animating -50% loops seamlessly. Hover
            pauses the scroll so the items stay editable in the builder. */}
        <style>{`@keyframes tpTickerScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } } .tp-ticker-track { animation: tpTickerScroll ${Math.max(24, tp.ticker.length * 6)}s linear infinite; } .tp-ticker-viewport:hover .tp-ticker-track { animation-play-state: paused; }`}</style>
        <div className="tp-ticker-viewport" style={{ overflow: "hidden", padding: "10px 0" }}>
          <div className="tp-ticker-track" style={{ display: "flex", alignItems: "center", width: "max-content" }}>
            {[0, 1].map((copy) => (
              <div key={copy} aria-hidden={copy === 1 || undefined} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                {tp.ticker.map((t, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
                    {copy === 0 ? (
                      <EditableText
                        value={t}
                        path={P("ticker", i)}
                        as="span"
                        style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#FDF4F0", whiteSpace: "nowrap" }}
                        placeholder="Ticker item"
                        testId={`text-temecula-passport-ticker-${i}`}
                        regenBlockKey={rk("ticker")}
                        regenAnchor={i === 0}
                      />
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#FDF4F0", whiteSpace: "nowrap" }}>{t}</span>
                    )}
                    <span style={{ color: BLUE, margin: "0 14px", fontSize: 12 }}>★</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Shell>
    ),
    header: () => (
      <Shell k="header" style={{ backgroundColor: CREAM, borderBottom: "1px solid rgba(96,10,44,0.08)" }}>
        <div style={{ ...wrap, display: "flex", justifyContent: "center", padding: "16px 20px" }}>
          <div style={{ width: 150, height: 44 }}>
            <MediaSlot slot="header-logo" label="Logo" testId="img-temecula-passport-header-logo" fallbackUrl={ctx.logoUrl} style={{ objectFit: "contain", minHeight: 44, borderRadius: 6 }} />
          </div>
        </div>
      </Shell>
    ),
    hero: () => (
      <Shell k="hero" style={{ backgroundColor: CREAM, padding: "48px 0 64px" }}>
        <div style={{ ...wrap, maxWidth: 1200 }} className="grid grid-cols-1 gap-9 md:grid-cols-2 lg:gap-12 md:items-start">
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, backgroundColor: NAVY, borderRadius: 999, padding: "6px 12px", marginBottom: 16 }}>
              <Flag className="h-3.5 w-3.5" style={{ color: "#fff", flexShrink: 0 }} />
              <EditableText value={tp.hero.badge} path={P("hero", "badge")} as="span" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#fff" }} placeholder="Badge" testId="text-temecula-passport-hero-badge" regenBlockKey={rk("hero")} />
            </div>
            {/* Hidden when the collab fields are blank (e.g. a brand with no
                collab partner) — even in the builder, so an intentionally
                blank row doesn't render as literal placeholder text. */}
            {Boolean(tp.hero.collabPrefix || tp.hero.collabHandle) && (
              <p style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: WINE }}>
                <EditableText value={tp.hero.collabPrefix} path={P("hero", "collabPrefix")} as="span" placeholder="Collab prefix" testId="text-temecula-passport-hero-collab-prefix" regenBlockKey={rk("hero")} />{" "}
                {editor ? (
                  <span className="group/lplink relative inline-flex" style={{ verticalAlign: "bottom" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: RED, fontWeight: 800 }}>
                      <Instagram className="h-3.5 w-3.5" />
                      <EditableText value={tp.hero.collabHandle} path={P("hero", "collabHandle")} as="span" placeholder="@handle" testId="text-temecula-passport-hero-collab-handle" regenBlockKey={rk("hero")} />
                    </span>
                    {editor.onEditLinkUrl && <LinkUrlPopover path={P("hero", "collabUrl")} url={tp.hero.collabUrl} testId="link-temecula-passport-hero-collab" />}
                  </span>
                ) : tp.hero.collabHandle ? (
                  <a href={tp.hero.collabUrl || undefined} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, color: RED, textDecoration: "none", fontWeight: 800 }} data-testid="link-temecula-passport-hero-collab">
                    <Instagram className="h-3.5 w-3.5" /> {tp.hero.collabHandle}
                  </a>
                ) : null}
              </p>
            )}
            <EditableText
              value={tp.hero.headline}
              path={P("hero", "headline")}
              as="h1"
              style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: "clamp(40px, 7vw, 68px)", lineHeight: 1.02, margin: 0, color: WINE, letterSpacing: "-0.01em" }}
              placeholder="Headline"
              // Reference-design two-tone: closing "is …" clause in red. Derived
              // from the single stored headline (see splitLpTemeculaHeadline);
              // editing still operates on the raw string.
              render={(v) => {
                const { main, accent } = splitLpTemeculaHeadline(v);
                if (!accent) return v;
                return (
                  <>
                    {main}
                    <span style={{ color: RED }}>{accent}</span>
                  </>
                );
              }}
              testId="text-temecula-passport-hero-headline"
              regenBlockKey={rk("hero")}
              regenAnchor
            />
            <EditableText
              value={tp.hero.subhead}
              path={P("hero", "subhead")}
              as="p"
              style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: "clamp(22px, 3.2vw, 32px)", lineHeight: 1.1, margin: "14px 0 0", color: RED }}
              placeholder="Subhead"
              testId="text-temecula-passport-hero-subhead"
              regenBlockKey={rk("hero")}
            />
            <EditableText
              value={tp.hero.body}
              path={P("hero", "body")}
              as="p"
              multiline
              style={{ fontSize: 17, lineHeight: 1.55, marginTop: 16, marginBottom: 26, maxWidth: 540, color: WINE, opacity: 0.85 }}
              placeholder="Body"
              testId="text-temecula-passport-hero-body"
              regenBlockKey={rk("hero")}
            />
            <div style={{ margin: "0 0 24px", display: "grid", gap: 8 }}>
              {tp.hero.bullets.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <CheckCircle2 className="h-4.5 w-4.5" style={{ width: 18, height: 18, color: BLUE, flexShrink: 0 }} />
                  <EditableText value={b} path={P("hero", "bullets", i)} as="span" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.5, color: WINE }} placeholder="Bullet" testId={`text-temecula-passport-hero-bullet-${i}`} regenBlockKey={rk("hero")} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: WINE, opacity: 0.85 }}>
              <span style={{ display: "inline-flex", gap: 2 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4" style={{ color: RED, fill: "currentcolor" }} />
                ))}
              </span>
              <EditableText value={tp.hero.socialProof} path={P("hero", "socialProof")} as="span" style={{ fontSize: 13, fontWeight: 700 }} placeholder="Social proof" testId="text-temecula-passport-hero-social" regenBlockKey={rk("hero")} />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Mystery drink card */}
            <div
              style={{
                backgroundColor: "#fff", padding: "14px 14px 16px", borderRadius: 18,
                border: `1px solid ${BORDER}`, boxShadow: "6px 6px 0 rgba(27,42,91,0.12)", transform: "rotate(-0.6deg)",
              }}
              data-testid="card-temecula-passport-mystery"
            >
              <div style={{ aspectRatio: "4 / 3", overflow: "hidden", borderRadius: 12, position: "relative", backgroundColor: WINE }}>
                <MediaSlot slot="mystery-card" label={tp.hero.mysteryCard.imageAlt} testId="img-temecula-passport-mystery" aiSlot={aiSlots["mystery-card"]} onRegenerate={retrySlot("mystery-card")} style={{ borderRadius: 12 }} />
                <Star className="h-5 w-5" style={{ position: "absolute", top: 18, left: 22, color: GOLD, fill: "currentcolor", opacity: 0.95, filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))", pointerEvents: "none" }} />
                <Star className="h-3.5 w-3.5" style={{ position: "absolute", top: 44, right: 34, color: "#fff", fill: "currentcolor", opacity: 0.9, filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))", pointerEvents: "none" }} />
                <Star className="h-4 w-4" style={{ position: "absolute", bottom: 28, right: 26, color: GOLD, fill: "currentcolor", opacity: 0.95, filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))", pointerEvents: "none" }} />
                <span style={{ position: "absolute", bottom: 14, left: 16, fontSize: 12, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#fff", textShadow: "0 2px 10px rgba(0,0,0,0.55)" }}>
                  <EditableText value={tp.hero.mysteryCard.overlayLabel} path={P("hero", "mysteryCard", "overlayLabel")} as="span" placeholder="Overlay label" testId="text-temecula-passport-mystery-overlay" regenBlockKey={rk("hero")} />
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, padding: "0 4px", gap: 8 }}>
                <EditableText value={tp.hero.mysteryCard.name} path={P("hero", "mysteryCard", "name")} as="span" style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.16em", textTransform: "uppercase", color: RED }} placeholder="Name" testId="text-temecula-passport-mystery-name" regenBlockKey={rk("hero")} />
                <EditableText value={tp.hero.mysteryCard.badge} path={P("hero", "mysteryCard", "badge")} as="span" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: NAVY, opacity: 0.8 }} placeholder="Badge" testId="text-temecula-passport-mystery-badge" regenBlockKey={rk("hero")} />
              </div>
            </div>
            <CouponPanel tp={tp} P={P} rk={rk} publicLeadContext={ctx.publicLeadContext} idSuffix="hero" anchor showSuccessEditor />
          </div>
        </div>
      </Shell>
    ),
    poster: () => (
      <Shell k="poster" style={{ backgroundColor: CREAM, padding: "16px 0 64px" }}>
        <div style={{ ...wrap, maxWidth: 760 }}>
          <div style={{ aspectRatio: "3 / 4", borderRadius: 18, overflow: "hidden", boxShadow: "0 24px 60px rgba(96,10,44,0.22)" }}>
            <MediaSlot slot="poster" label={tp.poster.alt} testId="img-temecula-passport-poster" aiSlot={aiSlots["poster"]} onRegenerate={retrySlot("poster")} style={{ borderRadius: 18 }} />
          </div>
        </div>
      </Shell>
    ),
    howItWorks: () => (
      <Shell k="howItWorks" style={{ backgroundColor: "#fff", padding: "72px 0", borderTop: "1px solid rgba(96,10,44,0.06)", borderBottom: "1px solid rgba(96,10,44,0.06)" }}>
        <div style={wrap}>
          <Eyebrow value={tp.howItWorks.eyebrow} path={P("howItWorks", "eyebrow")} testId="text-temecula-passport-how-eyebrow" />
          <Heading value={tp.howItWorks.heading} path={P("howItWorks", "heading")} testId="text-temecula-passport-how-heading" regenKey={rk("howItWorks")} size={44} />
          <EditableText value={tp.howItWorks.intro} path={P("howItWorks", "intro")} as="p" multiline style={{ maxWidth: 620, fontSize: 16, lineHeight: 1.55, color: WINE, opacity: 0.8, margin: "0 0 40px" }} placeholder="Intro" testId="text-temecula-passport-how-intro" regenBlockKey={rk("howItWorks")} />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {tp.howItWorks.steps.map((st, i) => {
              const Icon = STEP_ICONS[i % STEP_ICONS.length];
              const accent = i % 2 === 0 ? RED : NAVY;
              return (
                <div key={i} style={{ backgroundColor: CREAM, border: "1px solid rgba(96,10,44,0.08)", borderRadius: 16, padding: 28, borderTop: `4px solid ${accent}` }} data-testid={`card-temecula-passport-step-${i}`}>
                  <Icon className="h-7 w-7" style={{ color: accent }} />
                  <EditableText value={st.title} path={P("howItWorks", "steps", i, "title")} as="h3" style={{ fontSize: 20, fontWeight: 800, margin: "12px 0 8px", color: WINE, letterSpacing: "-0.01em" }} placeholder="Step title" testId={`text-temecula-passport-step-title-${i}`} regenBlockKey={rk("howItWorks")} />
                  <EditableText value={st.body} path={P("howItWorks", "steps", i, "body")} as="p" multiline style={{ fontSize: 14.5, lineHeight: 1.6, color: WINE, opacity: 0.85, margin: 0 }} placeholder="Step body" testId={`text-temecula-passport-step-body-${i}`} regenBlockKey={rk("howItWorks")} />
                </div>
              );
            })}
          </div>
        </div>
      </Shell>
    ),
    ingredients: () => (
      <Shell k="ingredients" style={{ backgroundColor: CREAM, padding: "80px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <Eyebrow value={tp.ingredients.eyebrow} path={P("ingredients", "eyebrow")} testId="text-temecula-passport-ingredients-eyebrow" center />
            <Heading value={tp.ingredients.heading} path={P("ingredients", "heading")} testId="text-temecula-passport-ingredients-heading" regenKey={rk("ingredients")} size={44} center />
            <EditableText value={tp.ingredients.intro} path={P("ingredients", "intro")} as="p" multiline style={{ fontSize: 17, lineHeight: 1.6, color: WINE, opacity: 0.85, margin: "0 auto", maxWidth: 600 }} placeholder="Intro" testId="text-temecula-passport-ingredients-intro" regenBlockKey={rk("ingredients")} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-stretch">
            <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
              {tp.ingredients.items.map((it, i) => {
                const Icon = INGREDIENT_ICONS[i % INGREDIENT_ICONS.length];
                const accent = i % 2 === 0 ? RED : NAVY;
                return (
                  <div key={i} style={{ display: "flex", gap: 16, backgroundColor: "#fff", border: "1px solid rgba(96,10,44,0.08)", borderRadius: 14, padding: "18px 20px", borderLeft: `4px solid ${accent}` }} data-testid={`card-temecula-passport-ingredient-${i}`}>
                    <div style={{ flexShrink: 0, width: 44, height: 44, borderRadius: "50%", backgroundColor: CREAM, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon className="h-5 w-5" style={{ color: accent }} />
                    </div>
                    <div>
                      <EditableText value={it.name} path={P("ingredients", "items", i, "name")} as="h3" style={{ fontSize: 17, fontWeight: 800, margin: "0 0 4px", color: WINE, letterSpacing: "-0.01em" }} placeholder="Name" testId={`text-temecula-passport-ingredient-name-${i}`} regenBlockKey={rk("ingredients")} />
                      <EditableText value={it.note} path={P("ingredients", "items", i, "note")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.55, color: WINE, opacity: 0.82, margin: 0 }} placeholder="Note" testId={`text-temecula-passport-ingredient-note-${i}`} regenBlockKey={rk("ingredients")} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(96,10,44,0.08)", backgroundColor: CREAM, minHeight: 280 }}>
              <MediaSlot slot="ingredients-feature" label={tp.ingredients.featureImageAlt} testId="img-temecula-passport-ingredients" aiSlot={aiSlots["ingredients-feature"]} onRegenerate={retrySlot("ingredients-feature")} style={{ borderRadius: 0 }} />
            </div>
          </div>
          <EditableText value={tp.ingredients.footnote} path={P("ingredients", "footnote")} as="p" style={{ fontSize: 12.5, color: WINE, opacity: 0.6, textAlign: "center", margin: "20px 0 0", lineHeight: 1.5 }} placeholder="Footnote" testId="text-temecula-passport-ingredients-footnote" regenBlockKey={rk("ingredients")} />
        </div>
      </Shell>
    ),
    gallery: () => (
      <Shell k="gallery" style={{ backgroundColor: "#fff", padding: "80px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <Eyebrow value={tp.gallery.eyebrow} path={P("gallery", "eyebrow")} testId="text-temecula-passport-gallery-eyebrow" center />
            <Heading value={tp.gallery.heading} path={P("gallery", "heading")} testId="text-temecula-passport-gallery-heading" regenKey={rk("gallery")} size={44} center />
            <EditableText value={tp.gallery.intro} path={P("gallery", "intro")} as="p" multiline style={{ fontSize: 17, lineHeight: 1.6, color: WINE, opacity: 0.85, margin: "0 auto", maxWidth: 600 }} placeholder="Intro" testId="text-temecula-passport-gallery-intro" regenBlockKey={rk("gallery")} />
          </div>
          <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4" style={{ gridAutoRows: 170 }}>
            {tp.gallery.items.slice(0, 4).map((g, i) => (
              <figure
                key={i}
                className={i === 0 ? "col-span-2 row-span-2" : i === 1 ? "col-span-2 row-span-1" : "col-span-1 row-span-1"}
                style={{ margin: 0, position: "relative", borderRadius: 14, overflow: "hidden", backgroundColor: CREAM, border: "1px solid rgba(96,10,44,0.08)" }}
                data-testid={`card-temecula-passport-gallery-${i}`}
              >
                <MediaSlot slot={`gallery-${i}`} label={g.alt} testId={`img-temecula-passport-gallery-${i}`} aiSlot={aiSlots[`gallery-${i}`]} onRegenerate={retrySlot(`gallery-${i}`)} style={{ borderRadius: 0 }} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0) 45%)", pointerEvents: "none" }} />
                <figcaption style={{ position: "absolute", left: 12, bottom: 12, color: "#fff", fontSize: 11.5, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}>
                  <EditableText value={g.caption} path={P("gallery", "items", i, "caption")} as="span" placeholder="Caption" testId={`text-temecula-passport-gallery-caption-${i}`} regenBlockKey={rk("gallery")} />
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </Shell>
    ),
    limitedBanner: () => (
      <Shell k="limitedBanner" style={{ padding: "88px 0", background: `linear-gradient(135deg, ${RED} 0%, ${WINE} 55%, ${NAVY} 100%)`, color: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 820, textAlign: "center" }}>
          <EditableText value={tp.limitedBanner.eyebrow} path={P("limitedBanner", "eyebrow")} as="div" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.85, marginBottom: 14 }} placeholder="Eyebrow" testId="text-temecula-passport-limited-eyebrow" />
          <EditableText
            value={tp.limitedBanner.heading}
            path={P("limitedBanner", "heading")}
            as="h2"
            style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontWeight: 900, fontSize: "clamp(32px, 4.6vw, 52px)", margin: 0, lineHeight: 1.05, color: "#fff" }}
            placeholder="Heading"
            testId="text-temecula-passport-limited-heading"
            regenBlockKey={rk("limitedBanner")}
            regenAnchor
          />
          <EditableText value={tp.limitedBanner.body} path={P("limitedBanner", "body")} as="p" multiline style={{ fontSize: 17, lineHeight: 1.6, opacity: 0.92, margin: "16px auto 32px", maxWidth: 600 }} placeholder="Body" testId="text-temecula-passport-limited-body" regenBlockKey={rk("limitedBanner")} />
          <a
            href={`#${TP_FORM_ANCHOR}`}
            onClick={scrollToForm}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, backgroundColor: "#fff", color: RED,
              padding: "15px 28px", borderRadius: 999, fontSize: 13, fontWeight: 900, letterSpacing: "0.08em",
              textTransform: "uppercase", textDecoration: "none", cursor: "pointer",
            }}
            data-testid="button-temecula-passport-limited-cta"
          >
            <EditableText value={tp.limitedBanner.cta} path={P("limitedBanner", "cta")} as="span" noHideDelete placeholder="CTA" testId="text-temecula-passport-limited-cta-label" regenBlockKey={rk("limitedBanner")} />
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </Shell>
    ),
    faq: () => (
      <Shell k="faq" style={{ backgroundColor: NAVY, padding: "88px 0", color: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 820 }}>
          <EditableText value={tp.faq.eyebrow} path={P("faq", "eyebrow")} as="div" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.8, marginBottom: 10 }} placeholder="Eyebrow" testId="text-temecula-passport-faq-eyebrow" />
          <EditableText
            value={tp.faq.heading}
            path={P("faq", "heading")}
            as="h2"
            style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontWeight: 900, fontSize: "clamp(32px, 4.4vw, 48px)", margin: 0, letterSpacing: "-0.01em", lineHeight: 1.05, color: "#fff" }}
            placeholder="Heading"
            testId="text-temecula-passport-faq-heading"
            regenBlockKey={rk("faq")}
            regenAnchor
          />
          <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 12 }}>
            {tp.faq.items.map((f, i) => (
              <div key={i} style={{ backgroundColor: "#fff", borderRadius: 12, padding: "18px 22px" }} data-testid={`card-temecula-passport-faq-${i}`}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <Minus className="h-4 w-4" style={{ color: RED, flexShrink: 0, marginTop: 4 }} />
                  <EditableText value={f.q} path={P("faq", "items", i, "q")} as="h3" style={{ fontSize: 16, fontWeight: 700, color: WINE, margin: 0 }} placeholder="Question" testId={`text-temecula-passport-faq-q-${i}`} regenBlockKey={rk("faq")} />
                </div>
                <EditableText value={f.a} path={P("faq", "items", i, "a")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: WINE, opacity: 0.85, margin: 0, paddingLeft: 26 }} placeholder="Answer" testId={`text-temecula-passport-faq-a-${i}`} regenBlockKey={rk("faq")} />
              </div>
            ))}
          </div>
        </div>
      </Shell>
    ),
    bottomCta: () => (
      <Shell k="bottomCta" style={{ backgroundColor: "#fff", padding: "72px 0" }}>
        <div style={{ ...wrap, maxWidth: 560, textAlign: "center" }}>
          <Eyebrow value={tp.bottomCta.eyebrow} path={P("bottomCta", "eyebrow")} testId="text-temecula-passport-bottom-eyebrow" center />
          <Heading value={tp.bottomCta.heading} path={P("bottomCta", "heading")} testId="text-temecula-passport-bottom-heading" regenKey={rk("bottomCta")} size={44} center />
          <EditableText value={tp.bottomCta.body} path={P("bottomCta", "body")} as="p" multiline style={{ fontSize: 17, lineHeight: 1.55, color: WINE, opacity: 0.85, margin: "12px auto 32px", maxWidth: 480 }} placeholder="Body" testId="text-temecula-passport-bottom-body" regenBlockKey={rk("bottomCta")} />
          <div style={{ textAlign: "left" }}>
            <CouponPanel tp={tp} P={P} rk={rk} publicLeadContext={ctx.publicLeadContext} idSuffix="bottom" />
          </div>
        </div>
      </Shell>
    ),
    pourBar: () => (
      <Shell k="pourBar" style={{ backgroundColor: "#fff", padding: "80px 0", position: "relative", overflow: "hidden" }}>
        <div style={{ ...wrap, position: "relative", zIndex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, backgroundColor: INK, borderRadius: 999, padding: "6px 14px", marginBottom: 14 }}>
              <MapPin className="h-3.5 w-3.5" style={{ color: RED }} />
              <EditableText value={tp.pourBar.badge} path={P("pourBar", "badge")} as="span" style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#fff" }} placeholder="Badge" testId="text-temecula-passport-pourbar-badge" regenBlockKey={rk("pourBar")} />
            </div>
            <EditableText
              value={tp.pourBar.heading}
              path={P("pourBar", "heading")}
              as="h2"
              style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontWeight: 900, fontSize: "clamp(36px, 5.6vw, 56px)", color: INK, margin: "0 0 14px", letterSpacing: "-0.02em", lineHeight: 1.05 }}
              placeholder="Heading"
              testId="text-temecula-passport-pourbar-heading"
              regenBlockKey={rk("pourBar")}
              regenAnchor
            />
            <EditableText value={tp.pourBar.intro} path={P("pourBar", "intro")} as="p" multiline style={{ fontSize: 17, color: "#5A554F", lineHeight: 1.6, maxWidth: 620, margin: "0 auto" }} placeholder="Intro" testId="text-temecula-passport-pourbar-intro" regenBlockKey={rk("pourBar")} />
          </div>
          <div className="grid grid-cols-1 gap-7 md:grid-cols-2 md:items-start">
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div style={{ display: "grid", gap: 14 }}>
                {tp.pourBar.slides.slice(0, 3).map((s, i) => (
                  <figure key={i} style={{ margin: 0 }} data-testid={`card-temecula-passport-slide-${i}`}>
                    <div style={{ aspectRatio: "4 / 3", borderRadius: 14, overflow: "hidden", backgroundColor: CREAM }}>
                      <MediaSlot slot={`pourbar-${i}`} label={s.alt} testId={`img-temecula-passport-slide-${i}`} aiSlot={aiSlots[`pourbar-${i}`]} onRegenerate={retrySlot(`pourbar-${i}`)} style={{ borderRadius: 14 }} />
                    </div>
                    {(editor || s.caption) && (
                      <figcaption style={{ marginTop: 8, textAlign: "center" }}>
                        <EditableText value={s.caption} path={P("pourBar", "slides", i, "caption")} as="span" style={{ fontSize: 12.5, fontWeight: 800, color: INK, letterSpacing: "0.04em", textTransform: "uppercase" }} placeholder="Caption" testId={`text-temecula-passport-slide-caption-${i}`} regenBlockKey={rk("pourBar")} />
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
              {(editor || tp.pourBar.stats.some((st) => st.num || st.label)) && (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 220px)", justifyContent: "center", gap: 10 }}>
                  {tp.pourBar.stats.map((st, i) =>
                    editor || st.num || st.label ? (
                      <div key={i} style={{ backgroundColor: CREAM, border: "1px solid #E8E0CB", borderRadius: 12, padding: "14px 12px", textAlign: "center" }} data-testid={`stat-temecula-passport-${i}`}>
                        <EditableText value={st.num} path={P("pourBar", "stats", i, "num")} as="div" style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: 30, fontWeight: 900, color: RED, lineHeight: 1, letterSpacing: "-0.02em" }} placeholder="Stat" testId={`text-temecula-passport-stat-num-${i}`} regenBlockKey={rk("pourBar")} />
                        <EditableText value={st.label} path={P("pourBar", "stats", i, "label")} as="div" style={{ fontSize: 10.5, fontWeight: 800, color: "#5A554F", margin: "6px 0 0", textTransform: "uppercase", letterSpacing: "0.08em", lineHeight: 1.3 }} placeholder="Label" testId={`text-temecula-passport-stat-label-${i}`} regenBlockKey={rk("pourBar")} />
                      </div>
                    ) : null,
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ backgroundColor: "#fff", border: `3px solid ${INK}`, boxShadow: `6px 6px 0 ${INK}`, transform: "rotate(-0.4deg)", borderRadius: 4, overflow: "hidden", display: "flex", flexDirection: "column" }} data-testid="card-temecula-passport-door-sign">
                <div style={{ backgroundColor: INK, padding: "10px 20px", textAlign: "center" }}>
                  <EditableText value={tp.pourBar.doorSign.header} path={P("pourBar", "doorSign", "header")} as="div" style={{ color: "#fff", fontSize: 12, fontWeight: 900, letterSpacing: "0.32em", textTransform: "uppercase" }} placeholder="Door sign header" testId="text-temecula-passport-door-header" regenBlockKey={rk("pourBar")} />
                </div>
                <div style={{ backgroundColor: RED, padding: "7px 20px", textAlign: "center", borderBottom: `2px solid ${INK}` }}>
                  <span style={{ color: "#fff", fontSize: 12, fontWeight: 900, letterSpacing: "0.32em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#fff", display: "inline-block", boxShadow: "0 0 0 3px rgba(255,255,255,0.25)" }} />
                    <EditableText value={tp.pourBar.doorSign.openBanner} path={P("pourBar", "doorSign", "openBanner")} as="span" placeholder="Open banner" testId="text-temecula-passport-door-open" regenBlockKey={rk("pourBar")} />
                  </span>
                </div>
                <div style={{ padding: "14px 24px" }}>
                  {(editor || tp.pourBar.doorSign.hours.some((h) => h.hours)) && (
                    <div style={{ fontSize: 10.5, fontWeight: 900, color: INK, letterSpacing: "0.24em", textAlign: "center", textTransform: "uppercase", margin: "0 0 8px", paddingBottom: 8, borderBottom: `1px solid ${INK}`, display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                      <Clock className="h-3.5 w-3.5" />
                      <EditableText value={tp.pourBar.doorSign.hoursLabel} path={P("pourBar", "doorSign", "hoursLabel")} as="span" placeholder="Hours label" testId="text-temecula-passport-door-hours-label" regenBlockKey={rk("pourBar")} />
                    </div>
                  )}
                  {tp.pourBar.doorSign.hours.map((h, i) =>
                    editor || h.hours ? (
                      <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0", borderBottom: i < tp.pourBar.doorSign.hours.length - 1 ? "1px dashed #D9D2BD" : "none" }} data-testid={`row-temecula-passport-hours-${i}`}>
                        <EditableText value={h.day} path={P("pourBar", "doorSign", "hours", i, "day")} as="span" style={{ fontSize: 15, fontWeight: 600, color: INK }} placeholder="Day" testId={`text-temecula-passport-hours-day-${i}`} regenBlockKey={rk("pourBar")} />
                        <EditableText value={h.hours} path={P("pourBar", "doorSign", "hours", i, "hours")} as="span" style={{ fontSize: 15, fontWeight: 900, color: INK, fontFamily: DISPLAY_FONT, fontStyle: "italic" }} placeholder="Hours" testId={`text-temecula-passport-hours-val-${i}`} regenBlockKey={rk("pourBar")} />
                      </div>
                    ) : null,
                  )}
                </div>
                <div style={{ borderTop: `3px solid ${INK}`, backgroundColor: CREAM, padding: "14px 24px", textAlign: "center" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", backgroundColor: INK, marginBottom: 6 }}>
                    <MapPin className="h-4 w-4" style={{ color: RED }} />
                  </span>
                  <EditableText value={tp.pourBar.doorSign.addressCallout} path={P("pourBar", "doorSign", "addressCallout")} as="p" style={{ fontSize: 12.5, fontWeight: 900, color: INK, margin: "0 0 1px", letterSpacing: "0.04em" }} placeholder="Address callout" testId="text-temecula-passport-door-callout" regenBlockKey={rk("pourBar")} />
                  {(editor || tp.pourBar.doorSign.address) && (
                    <EditableText value={tp.pourBar.doorSign.address} path={P("pourBar", "doorSign", "address")} as="p" style={{ fontSize: 15, fontWeight: 600, color: INK, lineHeight: 1.35, margin: "6px 0 0" }} placeholder="Address" testId="text-temecula-passport-door-address" regenBlockKey={rk("pourBar")} />
                  )}
                  {editor ? (
                    <span className="group/lplink relative inline-flex" style={{ margin: "8px 0 0" }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: RED, letterSpacing: "0.08em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <EditableText value={tp.pourBar.doorSign.mapsCta} path={P("pourBar", "doorSign", "mapsCta")} as="span" placeholder="Maps CTA" testId="text-temecula-passport-door-maps" regenBlockKey={rk("pourBar")} />
                        <ArrowRight className="h-3 w-3" />
                      </span>
                      {editor.onEditLinkUrl && <LinkUrlPopover path={P("pourBar", "doorSign", "mapsUrl")} url={tp.pourBar.doorSign.mapsUrl} testId="link-temecula-passport-door-maps" />}
                    </span>
                  ) : tp.pourBar.doorSign.mapsUrl && tp.pourBar.doorSign.mapsCta ? (
                    <a href={tp.pourBar.doorSign.mapsUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 800, color: RED, margin: "8px 0 0", letterSpacing: "0.08em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }} data-testid="link-temecula-passport-door-maps">
                      {tp.pourBar.doorSign.mapsCta}
                      <ArrowRight className="h-3 w-3" />
                    </a>
                  ) : null}
                </div>
              </div>
              {(editor || tp.pourBar.contacts.phone || tp.pourBar.contacts.email) && (
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" style={{ marginTop: 18 }}>
                  {(editor || tp.pourBar.contacts.phone) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, backgroundColor: CREAM, border: "1px solid #E8E0CB", borderRadius: 12, padding: "12px 14px" }} data-testid="card-temecula-passport-phone">
                      <Phone className="h-4 w-4" style={{ color: RED, flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <EditableText value={tp.pourBar.contacts.phoneLabel} path={P("pourBar", "contacts", "phoneLabel")} as="div" style={{ fontSize: 9.5, fontWeight: 900, color: "#7A6E66", letterSpacing: "0.14em", textTransform: "uppercase" }} placeholder="Phone label" testId="text-temecula-passport-phone-label" regenBlockKey={rk("pourBar")} />
                        {editor ? (
                          <span className="group/lplink relative inline-flex">
                            <span style={{ fontSize: 15, fontWeight: 800, color: INK }}>
                              <EditableText value={tp.pourBar.contacts.phone} path={P("pourBar", "contacts", "phone")} as="span" placeholder="Phone" testId="text-temecula-passport-phone" regenBlockKey={rk("pourBar")} />
                            </span>
                            {editor.onEditLinkUrl && <LinkUrlPopover path={P("pourBar", "contacts", "phoneHref")} url={tp.pourBar.contacts.phoneHref} testId="link-temecula-passport-phone" />}
                          </span>
                        ) : tp.pourBar.contacts.phone ? (
                          <a href={tp.pourBar.contacts.phoneHref || undefined} style={{ fontSize: 15, fontWeight: 800, color: INK, textDecoration: "none" }} data-testid="link-temecula-passport-phone">
                            {tp.pourBar.contacts.phone}
                          </a>
                        ) : null}
                      </div>
                    </div>
                  )}
                  {(editor || tp.pourBar.contacts.email) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, backgroundColor: CREAM, border: "1px solid #E8E0CB", borderRadius: 12, padding: "12px 14px" }} data-testid="card-temecula-passport-email">
                      <Mail className="h-4 w-4" style={{ color: RED, flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <EditableText value={tp.pourBar.contacts.emailLabel} path={P("pourBar", "contacts", "emailLabel")} as="div" style={{ fontSize: 9.5, fontWeight: 900, color: "#7A6E66", letterSpacing: "0.14em", textTransform: "uppercase" }} placeholder="Email label" testId="text-temecula-passport-email-label" regenBlockKey={rk("pourBar")} />
                        {editor ? (
                          <span className="group/lplink relative inline-flex">
                            <span style={{ fontSize: 15, fontWeight: 800, color: INK, wordBreak: "break-all" }}>
                              <EditableText value={tp.pourBar.contacts.email} path={P("pourBar", "contacts", "email")} as="span" placeholder="Email" testId="text-temecula-passport-email" regenBlockKey={rk("pourBar")} />
                            </span>
                            {editor.onEditLinkUrl && <LinkUrlPopover path={P("pourBar", "contacts", "emailHref")} url={tp.pourBar.contacts.emailHref} testId="link-temecula-passport-email" />}
                          </span>
                        ) : tp.pourBar.contacts.email ? (
                          <a href={tp.pourBar.contacts.emailHref || undefined} style={{ fontSize: 15, fontWeight: 800, color: INK, textDecoration: "none", wordBreak: "break-all" }} data-testid="link-temecula-passport-email">
                            {tp.pourBar.contacts.email}
                          </a>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Shell>
    ),
    footer: () => (
      <Shell k="footer" style={{ backgroundColor: WINE, color: "#fff", padding: "56px 0 28px" }}>
        <div style={{ ...wrap, maxWidth: 1200 }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ width: 140, height: 56, margin: "0 auto" }}>
              <MediaSlot slot="footer-logo" label="Footer logo" testId="img-temecula-passport-footer-logo" fallbackUrl={ctx.logoUrl} style={{ objectFit: "contain", minHeight: 56, borderRadius: 6 }} />
            </div>
            <EditableText value={tp.footer.tagline} path={P("footer", "tagline")} as="p" multiline style={{ fontSize: 15, color: "rgba(255,255,255,0.7)", margin: "12px auto 0", maxWidth: 460, lineHeight: 1.5 }} placeholder="Tagline" testId="text-temecula-passport-footer-tagline" regenBlockKey={rk("footer")} regenAnchor />
            {/* Hidden when blank (no collab partner) — even in the builder,
                matching the hero collab row. */}
            {Boolean(tp.footer.collabPrefix || tp.footer.collabHandle) && (
              <div style={{ marginTop: 18 }}>
                {editor ? (
                  <span className="group/lplink relative inline-flex" style={{ alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.25)", backgroundColor: "rgba(255,255,255,0.06)", fontSize: 14, fontWeight: 700 }}>
                    <Instagram className="h-4 w-4" style={{ color: BLUE }} />
                    <span style={{ color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                      <EditableText value={tp.footer.collabPrefix} path={P("footer", "collabPrefix")} as="span" placeholder="Collab prefix" testId="text-temecula-passport-footer-collab-prefix" regenBlockKey={rk("footer")} />
                    </span>
                    <EditableText value={tp.footer.collabHandle} path={P("footer", "collabHandle")} as="span" placeholder="@handle" testId="text-temecula-passport-footer-collab-handle" regenBlockKey={rk("footer")} />
                    {editor.onEditLinkUrl && <LinkUrlPopover path={P("footer", "collabUrl")} url={tp.footer.collabUrl} testId="link-temecula-passport-footer-collab" />}
                  </span>
                ) : tp.footer.collabHandle ? (
                  <a href={tp.footer.collabUrl || undefined} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.25)", backgroundColor: "rgba(255,255,255,0.06)", color: "#fff", textDecoration: "none", fontSize: 14, fontWeight: 700 }} data-testid="link-temecula-passport-footer-collab">
                    <Instagram className="h-4 w-4" style={{ color: BLUE }} />
                    <span style={{ color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>{tp.footer.collabPrefix}</span>
                    {tp.footer.collabHandle}
                  </a>
                ) : null}
              </div>
            )}
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }} data-testid="text-temecula-passport-footer-copyright">
              {tp.footer.copyright.replace("{year}", String(new Date().getFullYear()))}
            </div>
            <EditableText value={tp.footer.signoff} path={P("footer", "signoff")} as="div" style={{ fontFamily: DISPLAY_FONT, fontStyle: "italic", fontSize: 16, fontWeight: 700, color: RED }} placeholder="Signoff" testId="text-temecula-passport-footer-signoff" regenBlockKey={rk("footer")} />
          </div>
        </div>
      </Shell>
    ),
    stickyBar: () => (
      // Editor/preview: render inline (as the page's last section) so the bar
      // stays inside the preview canvas instead of position:fixed escaping to
      // the app viewport and covering the builder chrome (same pattern as the
      // coffee-shop sticky bar). Public page: pin it to the bottom.
      <Shell k="stickyBar" style={editor
        ? { backgroundColor: NAVY, borderTop: `2px solid ${RED}`, boxShadow: "0 -6px 24px rgba(0,0,0,0.25)" }
        : { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40, backgroundColor: NAVY, borderTop: `2px solid ${RED}`, boxShadow: "0 -6px 24px rgba(0,0,0,0.25)" }}>
        <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px" }}>
          <span style={{ minWidth: 0, overflow: "hidden" }}>
            <EditableText value={tp.stickyBar.label} path={P("stickyBar", "label")} as="span" style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }} placeholder="Sticky label" testId="text-temecula-passport-sticky-label" regenBlockKey={rk("stickyBar")} regenAnchor />
          </span>
          <a
            href={`#${TP_FORM_ANCHOR}`}
            onClick={scrollToForm}
            style={{
              backgroundColor: RED, color: "#fff", padding: "10px 20px", borderRadius: 999,
              fontSize: 12, fontWeight: 900, letterSpacing: "0.1em", textDecoration: "none",
              textTransform: "uppercase", whiteSpace: "nowrap", display: "inline-flex",
              alignItems: "center", gap: 8, cursor: "pointer",
            }}
            data-testid="button-temecula-passport-sticky-cta"
          >
            <EditableText value={tp.stickyBar.cta} path={P("stickyBar", "cta")} as="span" noHideDelete placeholder="Secret code" testId="text-temecula-passport-sticky-cta-label" regenBlockKey={rk("stickyBar")} />
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </Shell>
    ),
  };

  return (
    <div style={{ backgroundColor: CREAM, minHeight: "100vh", color: WINE, fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif' }} data-testid="temecula-passport-page">
      <LpReorderGroup
        groupKey="temeculaPassport:sections"
        items={LP_TEMECULA_PASSPORT_SECTIONS.map((s) => ({
          key: `temecula-passport-${s.key}`,
          node: renderers[s.key] ? <div key={`temecula-passport-${s.key}`}>{renderers[s.key]()}</div> : null,
        })).filter((it) => it.node)}
      />
    </div>
  );
}
